/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Box, Chip, Divider, TextField, Typography } from '@mui/material';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import { formatDate } from 'components/helpers/formatters';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { equipmentService } from 'shared/services';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { equipmentCheckListTemplateService } from '../api/checklist-template.service';
import { IChecklistTemplate, IEquipmentSummary } from '../types';
import { ChecklistTemplateSectionEditor } from './ChecklistTemplateSectionEditor';

interface ChecklistHistoryDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  instanceData: IChecklistTemplate | any;
}

export const ChecklistHistoryDetailsDialog = ({
  open,
  onClose,
  instanceData = {}
}: ChecklistHistoryDetailsDialogProps) => {
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [equipmentsOptions, setEquipmentsOptions] = useState<IEquipmentSummary[]>([]);
  const [frequencyTypes, setFrequencyTypes] = useState<Option[]>([]);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const [formInitialState, setFormInitialState] = useState<any>(() => ({
    name: instanceData?.name || '',
    description: instanceData?.description || '',
    notes: instanceData?.notes || '',
    frequency: instanceData?.frequency || '',
    equipments: Array.isArray(instanceData?.equipments) ? [...instanceData.equipments] : [],
    sections: Array.isArray(instanceData?.sections) ? [...instanceData.sections] : []
  }));

  useEffect(() => {
    setIsChanged(false);

    if (open) {
      equipmentService.getAllBaseInformation().then((res) => {
        if (res && Array.isArray(res)) {
          setEquipmentsOptions(
            res.map((eq) => ({
              id: eq.id,
              code: `${eq.description || ''} (SN ${eq.serial_number || ''})`,
              description: eq.description,
              serial_number: eq.serial_number,
              internal_id: eq.internal_id
            }))
          );
        }
      });

      equipmentCheckListTemplateService.options().then((res) => {
        const choices = res?.actions?.POST?.frequency?.choices;
        if (choices && Array.isArray(choices)) {
          setFrequencyTypes(
            choices.map((choice) => ({
              id: choice.value,
              code: choice.display_name
            }))
          );
        }
      });
    }
  }, [open]);

  useEffect(() => {
    if (open && instanceData) {
      const rawEquipments = Array.isArray(instanceData.equipments) ? instanceData.equipments : [];

      const formattedEquipments = rawEquipments.map((eq) => ({
        id: eq.id,
        code: eq.code?.includes('(SN')
          ? eq.code
          : `${eq.description || ''} (SN ${eq.serial_number || ''})`,
        description: eq.description,
        serial_number: eq.serial_number,
        internal_id: eq.internal_id
      }));

      setFormInitialState({
        name: instanceData.name || '',
        description: instanceData.description || '',
        notes: instanceData.notes || '',
        frequency: instanceData.frequency || '',
        equipments: formattedEquipments,
        sections: Array.isArray(instanceData.sections)
          ? JSON.parse(JSON.stringify(instanceData.sections))
          : []
      });
    }
  }, [instanceData, open]);

  const handleFieldChange = (field: string, value: any) => {
    setIsChanged(true);
    setFormInitialState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formInitialState.name.trim()) {
      toast.error('Il titolo della checklist è obbligatorio.');
      return;
    }

    if (!formInitialState.equipments || formInitialState.equipments.length === 0) {
      toast.error('Seleziona almeno uno strumento.');
      return;
    }

    setIsSubmitting(true);
    try {
      equipmentCheckListTemplateService.update(instanceData.id, {
        name: formInitialState.name.trim(),
        description: formInitialState.description.trim(),
        notes: formInitialState.notes.trim(),
        frequency: formInitialState.frequency,
        equipments: formInitialState.equipments.map((eq) => Number(eq.id)),
        sections: formInitialState.sections
      });
      toast.success('Modifiche su checklist salvate con successo!');
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EnhancedDialog
      open={open && !!instanceData}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={`Modifica Checklist: ${formInitialState.name}`}
      submitLabel={t('actions.save', 'Salva Modifiche')}
      cancelLabel={t('actions.cancel', 'Annulla')}
      isSubmitting={isSubmitting}
      isValid={isChanged}
      maxWidth="sm">
      <Box
        sx={{
          mb: 3,
          p: 1.5,
          bgcolor: '#f1f5f9',
          borderRadius: 2,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: 2
        }}>
        <Box sx={{ pb: { xs: 1, sm: 0 }, borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' } }}>
          <Typography
            variant="caption"
            sx={{ color: '#64748b', display: 'block', fontWeight: 600, fontSize: '0.7rem' }}>
            CREATO DA
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#1c2434',
              textTransform: 'capitalize',
              fontSize: '0.85rem',
              lineHeight: 1.3
            }}>
            {instanceData?.created_by_detail?.full_name ||
              instanceData?.created_by_detail?.username ||
              '—'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem', mt: 0.25 }}>
            {formatDate(instanceData?.created_at)}
          </Typography>
        </Box>

        <Box sx={{ pb: { xs: 1, sm: 0 }, borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' } }}>
          <Typography
            variant="caption"
            sx={{ color: '#64748b', display: 'block', fontWeight: 600, fontSize: '0.7rem' }}>
            ULTIMA MODIFICA
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#1c2434',
              textTransform: 'capitalize',
              fontSize: '0.85rem',
              lineHeight: 1.3
            }}>
            {instanceData?.last_modified_by_detail?.full_name ||
              instanceData?.last_modified_by_detail?.username ||
              '—'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem', mt: 0.25 }}>
            {formatDate(instanceData?.updated_at)}
          </Typography>
        </Box>

        <Box>
          <TextField
            select
            fullWidth
            label="FREQUENZA"
            value={formInitialState.frequency}
            onChange={(e) => handleFieldChange('frequency', e.target.value)}
            SelectProps={{ native: true }}
            variant="standard"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInputLabel-root': {
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.85rem',
                '&.Mui-focused': { color: '#29456c' }
              },
              '& native-select': {
                fontWeight: 600,
                color: '#1c2434',
                fontSize: '0.8rem',
                pt: 1
              }
            }}>
            <option value="">{t('Seleziona...')}</option>
            {frequencyTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.code}
              </option>
            ))}
          </TextField>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          required
          label="TITOLO CHECKLIST"
          value={formInitialState.name}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#1e293b',
              '& fieldset': { borderLeft: '4px solid #10b981', borderColor: '#e2e8f0' }
            }
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          size="small"
          label="DESCRIZIONE"
          value={formInitialState.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              fontSize: '0.825rem',
              fontWeight: 500,
              color: '#334155',
              '& fieldset': { borderLeft: '4px solid #1890ff', borderColor: '#e2e8f0' }
            }
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Autocomplete
          multiple
          options={equipmentsOptions}
          filterSelectedOptions
          value={formInitialState.equipments || []}
          onChange={(_, newValue) => handleFieldChange('equipments', newValue)}
          getOptionLabel={(option) => option.code || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                key={index}
                label={option.code}
                size="small"
                {...getTagProps({ index })}
                sx={{
                  bgcolor: '#e0f2fe',
                  color: '#0369a1',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  '& .MuiChip-deleteIcon': { color: '#0284c7' }
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              label="STRUMENTI ASSOCIATI"
              placeholder={
                formInitialState.equipments?.length ? '' : 'Seleziona uno o più strumenti...'
              }
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.85rem',
                  '& fieldset': { borderLeft: '4px solid #29456c', borderColor: '#e2e8f0' }
                }
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          size="small"
          label="NOTE INTERNE PER I TECNICI"
          value={formInitialState.notes}
          onChange={(e) => handleFieldChange('notes', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.85rem',
              '&.Mui-focused fieldset': { borderColor: '#29456c' }
            },
            '& .MuiInputLabel-root': {
              '&.Mui-focused': { color: '#29456c' }
            }
          }}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <ChecklistTemplateSectionEditor
        sections={formInitialState.sections}
        onChangeSections={(updatedSections) => {
          setIsChanged(true);
          setFormInitialState((prev) => ({ ...prev, sections: updatedSections }));
        }}
      />
    </EnhancedDialog>
  );
};
